package com.evoapps.evoshift.domain.usecase.leave

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.leave.LeaveBalances
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.model.InvalidLeaveAllowanceException
import com.evoapps.evoshift.domain.model.LeaveAllowance
import com.evoapps.evoshift.domain.model.LeaveEntitlement
import com.evoapps.evoshift.domain.model.TeamLeaveBalance
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.LeaveRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlin.time.Instant

/*
 * SH-113: annual leave balances. Who may call what is the screens' (Gate) and the rules'
 * (firestore.rules): the person reads their own figures and pay terms, the workplace's managers
 * everyone's figures; the pay terms (and with them the contract's entitlement) only owners and
 * financial managers.
 */

/** The workplace's market (Poland when the workplace cannot be read). */
private suspend fun WorkplaceRepository.marketOf(workplaceId: String): Market =
    getWorkplace(workplaceId).getOrNull()?.let { it.settings.market } ?: Market.POLAND

/**
 * [userId]'s own leave entitlement at [workplaceId] in [year]: the contract's (from their own pay
 * terms) and what a manager set. The balance itself comes from [LeaveBalances.balance] with the
 * person's absences, so a request shows against it at once.
 */
class GetMyLeaveEntitlementUseCase(
    private val leaveRepository: LeaveRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(userId: String, workplaceId: String, year: Int): Result<LeaveEntitlement> = runCatching {
        coroutineScope {
            val profile = async { userRepository.getUserProfile(userId).getOrNull() }
            val sensitive = async { userRepository.getSensitiveUserData(userId).getOrNull() }
            val allowances = async { leaveRepository.getAllowancesOf(userId, workplaceId).getOrThrow() }
            val market = async { workplaceRepository.marketOf(workplaceId) }
            LeaveBalances.entitlement(
                market = market.await(),
                userId = userId,
                workplaceId = workplaceId,
                year = year,
                financial = sensitive.await()?.financialEngagements?.get(workplaceId),
                engagement = profile.await()?.engagements?.get(workplaceId),
                allowance = allowances.await().firstOrNull { it.year == year }
            )
        }
    }
}

/**
 * Everyone's leave balance at [workplaceId] in [year], by name: the active team, their figures
 * and the workplace's absences. [withPayTerms]: read each person's pay terms for the contract's
 * entitlement (owners and financial managers); otherwise only a manager's figure is known.
 */
class GetTeamLeaveBalancesUseCase(
    private val leaveRepository: LeaveRepository,
    private val userRepository: UserRepository,
    private val absenceRepository: AbsenceRepository,
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(workplaceId: String, year: Int, withPayTerms: Boolean): Result<List<TeamLeaveBalance>> = runCatching {
        coroutineScope {
            val people = async { userRepository.getAssignableEmployees(workplaceId).getOrThrow() }
            val allowances = async { leaveRepository.getAllowances(workplaceId, year).getOrThrow().associateBy { it.userId } }
            val absences = async { absenceRepository.getAbsencesForWorkplace(workplaceId).getOrThrow() }
            val market = async { workplaceRepository.marketOf(workplaceId) }
            val team = people.await()
            val terms = if (withPayTerms) {
                team.map { person ->
                    async { person.uid to userRepository.getSensitiveUserData(person.uid).getOrNull()?.financialEngagements?.get(workplaceId) }
                }.awaitAll().toMap()
            } else emptyMap()
            val all = absences.await()
            team.map { person ->
                val financial = terms[person.uid]
                val entitlement = LeaveBalances.entitlement(
                    market = market.await(),
                    userId = person.uid,
                    workplaceId = workplaceId,
                    year = year,
                    financial = financial,
                    engagement = person.engagements[workplaceId],
                    allowance = allowances.await()[person.uid]
                )
                TeamLeaveBalance(
                    userId = person.uid,
                    displayName = person.displayName,
                    contract = financial?.contract,
                    entitlement = entitlement,
                    balance = LeaveBalances.balance(entitlement, all)
                )
            }.sortedBy { it.displayName.lowercase() }
        }
    }
}

/**
 * Saves a manager's leave figures for one person and year, stamped with [editorId]. Refuses days
 * outside 0..[LeaveAllowance.MAX_DAYS] ([InvalidLeaveAllowanceException]).
 */
class SaveLeaveAllowanceUseCase(
    private val leaveRepository: LeaveRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(allowance: LeaveAllowance, editorId: String): Result<LeaveAllowance> = runCatching {
        val range = 0..LeaveAllowance.MAX_DAYS
        if (allowance.carryOverDays !in range || allowance.entitlementDays?.let { it !in range } == true) {
            throw InvalidLeaveAllowanceException()
        }
        val stamped = allowance.copy(updatedAt = now().toString(), updatedBy = editorId)
        leaveRepository.saveAllowance(stamped).getOrThrow()
        stamped
    }
}
